import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, ChevronDown, ChevronRight, BarChart3, Users, Layers, TrendingUp } from 'lucide-react'
import {
  getTemplateAnalytics,
  getTemplateDerivedApps,
  type TemplateAnalytics,
  type DerivedApp,
} from '../../lib/api'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import { AnalyticsNav } from './analytics/AnalyticsOverview'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    running: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    stopped: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    deleted: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    starting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.stopped}`}>
      {status}
    </span>
  )
}

function SummaryCard({
  title,
  value,
  icon: Icon,
  subtitle,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  subtitle?: string
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-2xl font-semibold text-foreground">{value}</div>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  )
}

function DerivedAppsTable({ templateName }: { templateName: string }) {
  const [apps, setApps] = useState<DerivedApp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const data = await getTemplateDerivedApps(templateName)
        setApps(data)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load derived apps')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [templateName])

  if (loading) {
    return (
      <div className="px-8 py-3 text-sm text-muted-foreground">Loading derived apps...</div>
    )
  }

  if (error) {
    return (
      <div className="px-8 py-3 text-sm text-red-500">{error}</div>
    )
  }

  if (apps.length === 0) {
    return (
      <div className="px-8 py-3 text-sm text-muted-foreground">No apps created from this template yet.</div>
    )
  }

  return (
    <div className="px-8 py-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pb-2 font-medium">App Name</th>
            <th className="pb-2 font-medium">Owner</th>
            <th className="pb-2 font-medium">Status</th>
            <th className="pb-2 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => (
            <tr key={app.name} className="border-t border-border/50">
              <td className="py-2 text-foreground">
                {app.display_name || app.name}
                <span className="text-muted-foreground ml-1 text-xs">({app.name})</span>
              </td>
              <td className="py-2 text-muted-foreground">{app.owner_email || 'Unknown'}</td>
              <td className="py-2"><StatusBadge status={app.status} /></td>
              <td className="py-2 text-muted-foreground">{formatRelativeTime(app.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function TemplateAnalyticsPage() {
  const [analytics, setAnalytics] = useState<TemplateAnalytics[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null)

  const loadAnalytics = useCallback(async () => {
    try {
      const data = await getTemplateAnalytics()
      setAnalytics(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  useAutoRefresh(loadAnalytics, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  const totalTemplates = analytics.length
  const totalAppsCreated = analytics.reduce((sum, t) => sum + t.total_apps_created, 0)
  const mostPopular = analytics.length > 0
    ? analytics.reduce((a, b) => a.total_apps_created > b.total_apps_created ? a : b)
    : null

  const toggleExpand = (name: string) => {
    setExpandedTemplate(expandedTemplate === name ? null : name)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Template Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Usage stats and derived apps for all templates
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); loadAnalytics() }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Sub-navigation */}
      <AnalyticsNav />

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Templates"
          value={totalTemplates}
          icon={Layers}
          subtitle={`${analytics.filter(t => t.is_active).length} active`}
        />
        <SummaryCard
          title="Total Apps Created"
          value={totalAppsCreated}
          icon={Users}
          subtitle={`From ${analytics.filter(t => t.total_apps_created > 0).length} templates`}
        />
        <SummaryCard
          title="Most Popular"
          value={mostPopular?.template_display_name || mostPopular?.template_name || 'N/A'}
          icon={TrendingUp}
          subtitle={mostPopular ? `${mostPopular.total_apps_created} apps created` : undefined}
        />
      </div>

      {/* Templates table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Per-Template Stats
          </h2>
        </div>

        {loading && analytics.length === 0 ? (
          <div className="px-5 py-8 text-center text-muted-foreground">Loading analytics...</div>
        ) : analytics.length === 0 ? (
          <div className="px-5 py-8 text-center text-muted-foreground">
            No templates found. Create templates from the Templates page.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border bg-muted/30">
                <th className="px-5 py-3 font-medium w-8"></th>
                <th className="px-5 py-3 font-medium">Template</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Apps Created</th>
                <th className="px-5 py-3 font-medium text-right">Active</th>
                <th className="px-5 py-3 font-medium text-right">Stopped</th>
                <th className="px-5 py-3 font-medium text-right">Deleted</th>
                <th className="px-5 py-3 font-medium">Last Used</th>
              </tr>
            </thead>
            <tbody>
              {analytics.map((t) => (
                <>
                  <tr
                    key={t.template_name}
                    className="border-b border-border/50 hover:bg-muted/20 cursor-pointer transition-colors"
                    onClick={() => toggleExpand(t.template_name)}
                  >
                    <td className="px-5 py-3">
                      {expandedTemplate === t.template_name
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      }
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-foreground">
                        {t.template_display_name || t.template_name}
                      </div>
                      <div className="text-xs text-muted-foreground">{t.template_slug}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.is_active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-foreground">
                      {t.total_apps_created}
                    </td>
                    <td className="px-5 py-3 text-right text-green-600 dark:text-green-400">
                      {t.active_apps}
                    </td>
                    <td className="px-5 py-3 text-right text-muted-foreground">
                      {t.stopped_apps}
                    </td>
                    <td className="px-5 py-3 text-right text-red-500 dark:text-red-400">
                      {t.deleted_apps}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {formatDate(t.last_used)}
                    </td>
                  </tr>
                  {expandedTemplate === t.template_name && (
                    <tr key={`${t.template_name}-derived`} className="bg-muted/10">
                      <td colSpan={8} className="py-2">
                        <DerivedAppsTable templateName={t.template_name} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
